import React, {useEffect, useState} from 'react';
import {FormLabel, Select, Switch, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import authenticationManager from '../../Auth/AuthenticationManager';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button} from 'reactstrap';
import DocusignApiService from '../../api/DocusignApiService';
import SettingsApiService from '../../api/SettingsApiService';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';

export default function AdminSettings(props) {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const tempConsentCode = searchParams.get('code');

    const {isMobile, setSnackbarProperties} = props;

    const docusignApiService = new DocusignApiService();
    const settingsApiService = new SettingsApiService();
    const navigate = useNavigate();

    const [isDocusignEnabled, setIsDocusignEnabled] = useState(false);
    const [isDocusignEnabledSetting, setIsDocusignEnabledSetting] = useState(undefined);
    const [currentTemplateId, setCurrentTemplateId] = useState('');
    const [templates, setTemplates] = useState([]);
    const [docusignTemplateIdSetting, setDocusignTemplateIdSetting] = useState(undefined);
    const [currentDocusignConsent, setCurrentDocusignConsent] = useState(false);

    useEffect(() => {
        if(!authenticationManager.isAuthorized) {
            navigate('/');
        }

        void getDocusignAccountTemplates();

        void getDocusignEnabled();
        void isConsented();

        // After getting consent we'll have a temporary code. If we have it, we need to setup the docusign user
        if (tempConsentCode) {
            void setupDocusignUser(tempConsentCode);
        }
    }, [tempConsentCode]);

    async function isConsented() {
        const response = await docusignApiService.isConsented();

        if(response.success) {
            setCurrentDocusignConsent(response.data);
        } else {
            setSnackbarProperties('Error occurred when trying to check Docusign data.', 'error');
        }
    }

    async function getDocusignAccountTemplates() {
        const docusignAccountId = await settingsApiService.getSetting('DocusignAccountId');

        if (docusignAccountId.value) {
            const docusignTemplates = (await docusignApiService.getDocusignAccountTemplates()).data;
            setTemplates(docusignTemplates);

            const docusignTemplateId = await settingsApiService.getSetting('DocusignTemplateId');

            setDocusignTemplateIdSetting(docusignTemplateId);
            setCurrentTemplateId(docusignTemplates.find((template) => template.templateId === docusignTemplateId.value)?.templateId);
        }
    }

    async function getDocusignEnabled() {
        const response = await settingsApiService.getSetting('DocusignEnabled');

        setIsDocusignEnabledSetting(response);
        setIsDocusignEnabled(response.value === 'true');
    }

    async function getDocusignConsent() {
        const response = await docusignApiService.getDocusignConsent();

        if(!response.success) {
            setSnackbarProperties('Error occurred when trying to get Docusign Consent.', 'error');
        }
    }

    async function setupDocusignUser(tempConsentCode) {
        const response = await docusignApiService.setupDocusignUser(tempConsentCode);

        if(!response.success) {
            setSnackbarProperties('Error occurred when trying to setup Docusign User.', 'error');
        }

        // Remove the temp code from the URL.
        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState({}, document.title, url.pathname);
    }

    async function handleChange(event) {
        let updatedEnabledStatus = event.target.checked;
        isDocusignEnabledSetting.value = updatedEnabledStatus.toString();
        const settingUpdateResponse = await settingsApiService.updateSetting(isDocusignEnabledSetting);
        if(settingUpdateResponse) {
            setSnackbarProperties(`Docusign ${updatedEnabledStatus ? 'Enabled' : 'Disabled'} successfully`, 'success');
        } else {
            setSnackbarProperties('Error occurred when trying to change Docusign Enabled.', 'error');
        }

        setIsDocusignEnabled(updatedEnabledStatus);
    }

    async function changeTemplate(event) {
        let docusignTemplateSetting = docusignTemplateIdSetting;
        docusignTemplateSetting.value = event.target.value;

        const changeTemplateResponse  = await settingsApiService.updateSetting(docusignTemplateIdSetting);

        if(changeTemplateResponse) {
            setSnackbarProperties('Template changed successfully', 'success');
        } else {
            setSnackbarProperties('Error occurred when trying to change template.', 'error');
        }

        setDocusignTemplateIdSetting(docusignTemplateSetting);
        setCurrentTemplateId(event.target.value);
    }

    return(
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <h1>Settings</h1>
            </Grid>
            <Grid item xs={12}>
                <>
                    <FormLabel>Docusign Enabled</FormLabel>
                    <Switch
                        checked={isDocusignEnabled}
                        onChange={handleChange}
                        color="primary"
                    />
                </>
            </Grid>
            <Grid item xs={12}>
                <Button className={isMobile ? 'mobile-modal-button' : ''} autoFocus variant='contained'
                    onClick={getDocusignConsent}>
                    {currentDocusignConsent ? 'Change Docusign Consent' : 'Get Docusign Consent' }
                </Button>
            </Grid>
            <Grid item xs={12}>
                <Grid item xs={12}>
                    <Typography variant='p2'>
                        Docusign Templates
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Select
                        disabled={!currentDocusignConsent || !isDocusignEnabled}
                        value={currentTemplateId}
                        onChange={(event) => changeTemplate(event)}
                        variant={'standard'}
                        sx={{width: '300px'}}
                    >
                        {templates?.map((x) => {
                            return (<MenuItem key={x.templateId} value={x.templateId}>{x.name}</MenuItem>);
                        })}
                        {templates.length === 0 && currentDocusignConsent &&
                            <MenuItem key={0} value={0}>No templates found</MenuItem>
                        }
                        {!currentDocusignConsent &&
                            <MenuItem key={0} value={0}>Need to get Docusign consent</MenuItem>
                        }
                    </Select>
                </Grid>
            </Grid>
        </Grid>
    );
}

AdminSettings.propTypes = {
    isMobile: PropTypes.bool,
    setSnackbarProperties: PropTypes.func,
};
